/* eslint-disable @typescript-eslint/no-explicit-any */
import { CLIENT_ID_KEY, PROJECT_ID_KEY } from '../models/feature-flag';
import { Injectable, Optional, Inject, Injector, InjectionToken } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, map, switchMap, take } from 'rxjs';
import { initialize } from 'launchdarkly-js-client-sdk';
import { Configuration } from '@content-platform/web-angular-shared-util-configuration';
import { featureFlagActions } from '../actions';
import { featureFlagSelectors } from '../selectors';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';
import { FeatureFlagUserContextInitService } from './feature-flag-user-context-init.service';
import { InHouseFeatureFlagClient } from './in-house-feature-flag-client';
import * as i0 from "@angular/core";
import * as i1 from "@ngrx/store";
import * as i2 from "@content-platform/web-angular-shared-util-logging";
import * as i3 from "./feature-flag-user-context-init.service";
/** Runtime-config keys for the in-house feature-flag backend (all under the `launchDarkly` block). */
const USE_IN_HOUSE_BACKEND_KEY = 'launchDarkly.useInHouseBackend';
const GRAPHQL_URL_KEY = 'launchDarkly.graphqlUrl';
/**
 * Token that carries authenticated-user info for the actual-user LD evalx call.
 * Provided by `WebAngularSharedDataAccessOktaAuth2Module` (bridged from
 * PRE_BOOTSTRAP_USER_INFO). Defined here — inside feature-flag-api — so that
 * okta-auth2 can import it without creating a circular dependency.
 */
export const FEATURE_FLAG_USER_INFO = new InjectionToken('feature-flag-user-info');
export var FlagConfigState;
(function (FlagConfigState) {
    FlagConfigState["loading"] = "loading";
    FlagConfigState["ready"] = "ready";
})(FlagConfigState || (FlagConfigState = {}));
/** Console style for LaunchDarkly config warnings - orange badge, visible in dev tools */
const LAUNCHDARKLY_WARN_STYLE = 'background: #e65100; color: white; font-weight: bold; padding: 2px 8px; border-radius: 3px;';
export class WebAngularSharedDataAccessFeatureFlagApisService {
    constructor(store, logger, featureFlagUserContextInit, injector, featureFlagUserInfo) {
        this.store = store;
        this.logger = logger;
        this.featureFlagUserContextInit = featureFlagUserContextInit;
        this.injector = injector;
        this.featureFlagUserInfo = featureFlagUserInfo;
        this.flagConfigReadyS = new BehaviorSubject(FlagConfigState.loading);
        /** True when this app/env opted into the in-house AppSync backend via secret; default false = LaunchDarkly. */
        this.useInHouseBackend = false;
        this.logger = logger.instance('WebAngularSharedDataAccessFeatureFlagApisService');
        this.flagConfigReady$ = this.flagConfigReadyS.asObservable();
    }
    /**
     * Retrieves client key and initializes LaunchDarkly client.
     * If LaunchDarkly config is missing (e.g. new app before secret is created),
     * marks flags as ready and skips initialization so the app can load.
     */
    configureClient() {
        const ldConfig = Configuration.get('launchDarkly');
        if (!ldConfig || typeof ldConfig !== 'object') {
            console.warn('%c LaunchDarkly %c Config not available – feature flags disabled. Add launchDarkly to app secret when ready.', LAUNCHDARKLY_WARN_STYLE, 'color: #e65100; font-weight: 500;');
            this.logger.warn('LaunchDarkly config not available - feature flags disabled. Add launchDarkly to app secret when ready.');
            this.flagConfigReadyS.next(FlagConfigState.ready);
            return;
        }
        // Reversible per-app runtime switch. Default (key absent or false) = LaunchDarkly, in every
        // env. Only when the app's effective secret sets it true do we route to the in-house backend.
        // The value read here is already the server-side merge of globals + per-app (+ local config),
        // so the global/per-app knobs work with no extra code.
        this.useInHouseBackend = Configuration.get(USE_IN_HOUSE_BACKEND_KEY) === true;
        if (this.useInHouseBackend) {
            this.configureInHouseClient(ldConfig);
            return;
        }
        const genericUser = ldConfig['genericUser'];
        this.projectId = ldConfig[PROJECT_ID_KEY];
        const ldClientKey = ldConfig[CLIENT_ID_KEY];
        const flags = ldConfig['flags'];
        if (!this.projectId || !ldClientKey) {
            console.warn('%c LaunchDarkly %c Settings incomplete – feature flags disabled. Check clientId / projectId in app secret.', LAUNCHDARKLY_WARN_STYLE, 'color: #e65100; font-weight: 500;', ldConfig);
            this.logger.warn('LaunchDarkly settings incomplete - feature flags disabled.', { launchDarkly: ldConfig });
            this.flagConfigReadyS.next(FlagConfigState.ready);
            return;
        }
        if (genericUser) {
            if (ldConfig['userBasedFlags']) {
                this.setClient(ldClientKey, genericUser, flags, FlagConfigState.loading);
                this.setClientAsync(ldClientKey, flags);
            }
            else {
                this.setClient(ldClientKey, genericUser, flags, FlagConfigState.ready);
                this.flagConfigReadyS.next(FlagConfigState.ready);
            }
        }
        else {
            this.setClientAsync(ldClientKey, flags);
        }
    }
    /**
     * In-house AppSync backend path. Resolves the internal client lazily (only when opted in) so
     * LaunchDarkly apps never construct the AppSync/Okta transport. Drives readiness + the NgRx seam
     * exactly like the LD path (SetFlags -> INITIALIZATION_COMPLETE -> getFlagAsync unblocks).
     */
    configureInHouseClient(ldConfig) {
        const projectKey = ldConfig[PROJECT_ID_KEY];
        // clientId is the per-(project, env) credential from the app's secret and is the ENV SELECTOR
        // for the in-house backend: the AppSync evaluate query + onFlagChange subscription are keyed by
        // (projectKey, clientId). The environment is therefore pinned by config, NOT derived from the URL.
        const clientId = ldConfig[CLIENT_ID_KEY];
        const graphqlUrl = Configuration.get(GRAPHQL_URL_KEY);
        if (!projectKey || !clientId || !graphqlUrl) {
            console.warn('%c FeatureFlags %c In-house backend enabled but projectId / clientId / graphqlUrl missing – feature flags disabled.', LAUNCHDARKLY_WARN_STYLE, 'color: #e65100; font-weight: 500;', { projectId: projectKey, clientId, graphqlUrl });
            this.logger.warn('In-house feature-flag settings incomplete - feature flags disabled.', {
                projectId: projectKey,
                clientId,
                graphqlUrl
            });
            // Dispatch an empty flag set so INITIALIZATION_COMPLETE fires and getFlagAsync() unblocks.
            this.store.dispatch(new featureFlagActions.SetFlags({ flags: {}, project: projectKey }));
            this.flagConfigReadyS.next(FlagConfigState.ready);
            return;
        }
        this.projectId = projectKey;
        const config = { projectKey, clientId, graphqlUrl };
        const client = this.injector.get(InHouseFeatureFlagClient);
        client
            .start(config, this.featureFlagUserInfo)
            .pipe(take(1))
            .subscribe({
            next: () => this.flagConfigReadyS.next(FlagConfigState.ready),
            // Even on failure, mark ready so the readiness guard lets the app load (mirrors LD fallbacks).
            error: () => this.flagConfigReadyS.next(FlagConfigState.ready)
        });
    }
    loadFlagsForUser(ldContext) {
        if (this.useInHouseBackend) {
            this.injector.get(InHouseFeatureFlagClient).identify(this.toFeatureFlagUserInfo(ldContext));
            return;
        }
        if (!this.appClient) {
            return;
        }
        this.appClient.identify(ldContext, null, (err, flags) => {
            if (err) {
                this.logger.error('launchdarkly initialization failed with error: ', err);
                return;
            }
            this.store.dispatch(new featureFlagActions.SetFlags({
                flags: flags,
                project: this.projectId
            }));
        });
    }
    /** Map a LaunchDarkly user context to the minimal identity the in-house evaluate query needs. */
    toFeatureFlagUserInfo(ldContext) {
        if (!ldContext || typeof ldContext !== 'object') {
            return null;
        }
        const context = ldContext;
        return {
            email: context.email,
            name: context.name,
            sub: context.key
        };
    }
    /**
     * Returns the value of the flag
     * Checks if the flag is available app level first and then global level
     * Otherwise throws an error
     * @param flagname
     * @param flaglevel
     */
    getFlag(flagname) {
        const flags = this.getAllFlags();
        if (flags[flagname] !== undefined) {
            return flags[flagname];
        }
        else {
            this.logger.error(`NOT_FOUND: ${flagname} does not exists`);
            return false;
        }
    }
    /**
     * Returns an observable to retrieve the value of the flag
     * Checks if the flag is available app level first and then global level
     * Otherwise throws an error
     * @param flagname
     * @param flaglevel
     */
    // tslint:disable-next-line:no-any
    getFlagAsync(flagname) {
        const appFlag$ = this.store.select(featureFlagSelectors.getAppFeatureFlag({ flag: flagname }));
        return this.isFeatureFlagsInitializationComplete().pipe(filter((initializationComplete) => !!initializationComplete), switchMap(() => combineLatest([appFlag$]).pipe(map(([appRes]) => {
            if (appRes !== undefined) {
                return appRes;
            }
            else {
                this.logger.error(`NOT_FOUND: ${flagname} does not exists`);
                return false;
            }
        }))));
    }
    shutdownClient() {
        if (this.useInHouseBackend) {
            this.injector.get(InHouseFeatureFlagClient).stop();
            return;
        }
        if (this.appClient) {
            this.appClient.close();
        }
    }
    trackEvent(eventName, prop) {
        // In-house backend has no analytics track API yet — safe no-op (future hook).
        if (this.useInHouseBackend || !this.appClient) {
            return;
        }
        this.appClient.track(eventName, prop);
    }
    getAsyncFlagVariation(flagname, variation) {
        const appFlag$ = this.store.select(featureFlagSelectors.getAppFeatureFlag({ flag: flagname }));
        return this.isFeatureFlagsInitializationComplete().pipe(filter((initializationComplete) => !!initializationComplete), switchMap(() => combineLatest([appFlag$]).pipe(map(([appRes]) => {
            // In-house mode: the store already holds the resolved (multivariate) value; there is
            // no LDClient to consult, so serve the cached value or fall back to the default.
            if (this.useInHouseBackend) {
                return appRes !== undefined ? appRes : variation;
            }
            if (!this.appClient) {
                return undefined;
            }
            if (appRes !== undefined && variation) {
                return this.appClient.variation(flagname, variation);
            }
            return this.appClient.variation(flagname, undefined);
        }))));
    }
    getAsyncFlagVariationDetails(flagname, defaultValue) {
        const appFlag$ = this.store.select(featureFlagSelectors.getAppFeatureFlag({ flag: flagname }));
        return this.isFeatureFlagsInitializationComplete().pipe(filter((initializationComplete) => !!initializationComplete), switchMap(() => combineLatest([appFlag$]).pipe(map(([appRes]) => {
            // In-house mode: no LDClient variationDetail; return an LD-shaped detail object from the
            // store cache so callers reading `.value` / `.variationIndex` / `.reason` keep working.
            if (this.useInHouseBackend) {
                return {
                    value: appRes !== undefined ? appRes : defaultValue,
                    variationIndex: null,
                    reason: null
                };
            }
            if (!this.appClient) {
                return undefined;
            }
            if (appRes !== undefined && defaultValue) {
                return this.appClient.variationDetail(flagname, defaultValue);
            }
            return undefined;
        }))));
    }
    isFeatureFlagsInitializationComplete() {
        return this.store.select(featureFlagSelectors.getFeatureFlagInitializationStatus);
    }
    setClientAsync(ldClientKey, flags) {
        if (this.featureFlagUserInfo) {
            const userKey = this.featureFlagUserInfo.email ?? this.featureFlagUserInfo.sub;
            if (!userKey) {
                this.finishFlagInitializationWithoutClient(flags);
                return;
            }
            const ldContext = {
                kind: 'user',
                key: userKey,
                name: this.featureFlagUserInfo.name ?? userKey,
                email: this.featureFlagUserInfo.email
            };
            // setTimeout(0) registers a macrotask AFTER the generic-user LD SDK request
            // (which was registered synchronously in setClient above), guaranteeing 2 separate evalx calls.
            setTimeout(() => {
                this.setClient(ldClientKey, ldContext, flags, FlagConfigState.ready);
            }, 0);
            return;
        }
        // Fallback for apps that don't provide FEATURE_FLAG_USER_INFO
        this.featureFlagUserContextInit.subscribeOnce({
            onContext: (context) => {
                this.setClient(ldClientKey, context, flags, FlagConfigState.ready);
            },
            onMissingOrError: () => {
                this.finishFlagInitializationWithoutClient(flags);
            }
        });
    }
    /**
     * User-based LD init failed before a client existed. Still dispatch SetFlags so the
     * INITIALIZATION_COMPLETE effect runs; otherwise getFlagAsync() waits forever on
     * getFeatureFlagInitializationStatus.
     */
    finishFlagInitializationWithoutClient(flags) {
        this.store.dispatch(new featureFlagActions.SetFlags({
            flags: flags ?? {},
            project: this.projectId
        }));
        this.flagConfigReadyS.next(FlagConfigState.ready);
    }
    setClient(ldClientKey, ldContext, flags, flagConfigState) {
        this.appClient = this.initializeClient(ldClientKey, ldContext, this.projectId, flags, flagConfigState);
    }
    /**
     * Initializes feature flag client
     * @param clientKey launchdarkly project specific client key
     * @param userObject  launchdarkly user object
     * @param projectKey launchDarkly project identifier
     */
    initializeClient(clientKey, userObject, projectKey, flags, flagConfigState) {
        const client = initialize(clientKey, userObject, {
            evaluationReasons: true,
            bootstrap: 'localStorage',
        });
        client
            .waitForInitialization()
            .then(() => {
            this.store.dispatch(new featureFlagActions.SetFlags({
                flags: this.appClient.allFlags(),
                project: projectKey
            }));
            this.flagConfigReadyS.next(flagConfigState);
        })
            .catch((err) => {
            this.store.dispatch(new featureFlagActions.SetFlags({
                flags: flags,
                project: projectKey
            }));
            this.logger.error('launchdarkly initialization failed with error: ', err);
        });
        client.on('change', (changes) => {
            this.store.dispatch(new featureFlagActions.UpdateFlags({ changes: changes, project: projectKey }));
        });
        return client;
    }
    /**
     * Retrieves all feature flags
     * @returns feature flags
     */
    getAllFlags() {
        if (!this.appClient) {
            const ldConfig = Configuration.get('launchDarkly');
            return (ldConfig && ldConfig.flags) ? ldConfig.flags : {};
        }
        return this.appClient.allFlags();
    }
    static { this.ɵfac = function WebAngularSharedDataAccessFeatureFlagApisService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || WebAngularSharedDataAccessFeatureFlagApisService)(i0.ɵɵinject(i1.Store), i0.ɵɵinject(i2.LoggerService), i0.ɵɵinject(i3.FeatureFlagUserContextInitService), i0.ɵɵinject(i0.Injector), i0.ɵɵinject(FEATURE_FLAG_USER_INFO, 8)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: WebAngularSharedDataAccessFeatureFlagApisService, factory: WebAngularSharedDataAccessFeatureFlagApisService.ɵfac, providedIn: 'root' }); }
}
//# sourceMappingURL=feature-flags.service.js.map